import EditIcon from "@mui/icons-material/Edit";
import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import CustomSelect from "../../../components/Select/Select";
import { getDiagnosticTemplatesWoLoader } from "../../../redux/reducers/clinicSlice";
import { deleteTreatmentPlanById } from "../../../redux/reducers/planSlice";
import { AppColors } from "../../../util/AppColors";
import { eyeBtn } from "../../../util/CommonIconStyle";
import { diagTemplateTypes } from "../../../util/dropList";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import DiagnosticTemplateCreate from "./DiagnosticTemplateCreate";

const tableHeader = [
  "templateName",
  "createdBy",
  "linkedServices",
  "lastUpdated",
  "extraStatus",
];
const initValues = {
  visible: false,
  name: "",
  type: "parameter",
  linkedServices: [],
  error: false,
};

const DiagnosticTemplate = () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [createValues, setCreateValues] = useState(initValues);
  const [createVsble, setCreateVsble] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [delModVsble, setDelModVsble] = useState(false);
  const [selectedTemp, setSelectedTemp] = useState(null);
  const { diagnosticTemplates, details: clinicDet } = useSelector(
    (state) => state?.clinic,
  );
  const itemsPerLoad = 10;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    mandateServices,
    clinicVets,
    clinicFromSession,
    mandateDiagnosticTemplates,
  } = useMemo(() => {
    if (!mandateDataRaw) {
      return {
        mandateServices: [],
        clinicVets: [],
        clinicFromSession: {},
        mandateDiagnosticTemplates: [],
      };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        mandateServices: Array.isArray(reqData?.services)
          ? reqData.services
          : [],
        clinicVets: Array.isArray(reqData?.vets) ? reqData.vets : [],
        clinicFromSession: reqData?.clinic ?? {},
        mandateDiagnosticTemplates: Array.isArray(reqData?.diagnosticTemplates)
          ? reqData.diagnosticTemplates
          : [],
      };
    } catch {
      return {
        mandateServices: [],
        clinicVets: [],
        clinicFromSession: {},
        mandateDiagnosticTemplates: [],
      };
    }
  }, [mandateDataRaw]);
  const clinicDetails = clinicFromSession?.id ? clinicFromSession : clinicDet;

  const serviceList = useMemo(
    () =>
      (mandateServices ?? [])
        ?.filter((ser) => ser?.active !== "N")
        ?.filter((ser) =>
          ["lab", "radiology"].includes(
            String(ser?.category ?? "")?.toLowerCase(),
          ),
        )
        ?.map((ser) => ({
          name: ser?.serviceName,
          serviceName: ser?.serviceName,
          value: ser?.serviceId ?? ser?.serviceName,
          serviceId: ser?.serviceId,
          category: ser?.category ?? "",
          subCategory: ser?.subCategory ?? "",
        })),
    [mandateServices],
  );

  const templates = useMemo(() => {
    const fromRedux = diagnosticTemplates?.list ?? [];
    if (Array.isArray(fromRedux) && fromRedux.length > 0) {
      return fromRedux;
    }
    return Array.isArray(mandateDiagnosticTemplates)
      ? mandateDiagnosticTemplates
      : [];
  }, [diagnosticTemplates, mandateDiagnosticTemplates]);

  const filteredTemplates = useMemo(() => {
    const q = searchValue?.trim()?.toLowerCase();
    if (!q) return templates;
    return (templates ?? [])?.filter((tmp) =>
      (tmp?.templateName ?? tmp?.name ?? "")?.toLowerCase()?.includes(q),
    );
  }, [templates, searchValue]);

  const pagedTemplates = useMemo(() => {
    const start = (page - 1) * itemsPerLoad;
    return (filteredTemplates ?? [])?.slice(start, start + itemsPerLoad);
  }, [filteredTemplates, page, itemsPerLoad]);

  useEffect(() => {
    onApiCall();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchValue]);

  const onApiCall = () => {
    dispatch(getDiagnosticTemplatesWoLoader("?page=1&itemSize=1000")).then(
      (res) => {
        if (res?.payload == null) return;
        const list = res?.payload?.list ?? res?.payload?.data ?? [];
        patchMandateSession({
          diagnosticTemplates: Array.isArray(list) ? list : [],
        });
      },
    );
  };

  const handleChangePage = (_e, selectedPage) => {
    setPage(selectedPage);
  };

  const handleDelete = (det) => {
    setSelectedTemp(det);
    setDelModVsble(true);
  };

  const handleEdit = (det) => {
    setSelectedTemp(det);
    setCreateVsble(true);
  };

  const handleCloseDel = () => {
    setSelectedTemp(null);
    setDelModVsble(false);
  };

  const handleConfirmDel = () => {
    dispatch(deleteTreatmentPlanById(selectedTemp?.templateId)).then((res) => {
      if (res?.payload) {
        onApiCall();
        handleCloseDel();
      }
    });
  };

  const getCreatedByName = (createdBy) => {
    if (!createdBy) return "-";
    const vetMatch = clinicVets?.find(
      (vet) => String(vet?.doctorId) === String(createdBy),
    );
    if (vetMatch?.name) return vetMatch.name;
    if (String(clinicDetails?.id) === String(createdBy)) {
      return clinicDetails?.name ?? "-";
    }
    return "-";
  };

  const getLinkedServiceLabel = (pln) => {
    const linked = pln?.linkedServices ?? pln?.serviceIds ?? [];
    if (!linked?.length) return "-";
    return (
      linked
        ?.map((ser) => {
          const id =
            typeof ser === "object" && ser !== null
              ? (ser?.serviceId ?? ser?.value)
              : ser;
          if (id == null || id === "") return "";
          const match = serviceList?.find(
            (s) => String(s?.serviceId) === String(id),
          );
          return match?.serviceName ?? match?.name ?? String(id);
        })
        ?.filter(Boolean)
        ?.join(", ") || "-"
    );
  };

  const getTableList = () => {
    return (pagedTemplates ?? [])?.map((pln) => ({
      templateName: pln?.templateName,
      createdBy: getCreatedByName(pln?.createdBy ?? pln?.clinicId),
      linkedServices: getLinkedServiceLabel(pln),
      lastUpdated: moment(new Date(pln?.createdDate)).format("DD MMM YYYY"),
      extraStatus: (
        <div className="flex-row">
          {/* <div className="cursor" onClick={() => handleDelete(pln)}>
            <div className="flex-center">
              <DeleteOutlineIcon sx={delBtn} />
            </div>
            <Typography className="red3 fs8 txt-semi-bold text-align-center">
              Delete
            </Typography>
          </div> */}
          <div className="cursor ml10" onClick={() => handleEdit(pln)}>
            <div className="flex-center">
              <EditIcon sx={eyeBtn} />
            </div>
            <Typography className="blue-color fs8 txt-semi-bold text-align-center">
              Edit
            </Typography>
          </div>
        </div>
      ),
    }));
  };

  const handleCreate = () => {
    setCreateValues({ ...createValues, visible: true });
    // setSelectedTemp(null);
    // setCreateVsble(true);
  };

  const onNext = () => {
    if (createValues?.name?.length === 0 || !createValues?.name) {
      return setCreateValues({ ...createValues, error: true });
    }
    setCreateVsble(true);
    setSelectedTemp({
      templateName: createValues?.name,
      type: createValues?.type,
      linkedServices: (createValues?.linkedServices ?? [])
        ?.filter((ser) => ser?.serviceId || ser?.value)
        ?.map((ser) => ser?.serviceId ?? ser?.value),
    });
    setCreateValues(initValues);
  };

  const onCloseCreateMod = () => {
    setCreateValues(initValues);
  };

  const onCreateValueChange = (name, value) => {
    const reqObj = { ...createValues, [name]: value };
    if (name === "name") {
      reqObj.error = value?.length > 0 ? false : true;
    }
    setCreateValues(reqObj);
  };

  return (
    <>
      {createVsble ? (
        <DiagnosticTemplateCreate
          setCreateVsble={setCreateVsble}
          selectedTemp={selectedTemp}
          setSelectedTemp={setSelectedTemp}
        />
      ) : null}
      {!createVsble && (
        <div>
          <div className="flex-row-ali-cen flex-center mt5">
            <div className="w30Per">
              <CustomTextField
                placeholder="Search"
                name="name"
                fullWidth
                handleChange={(e) => setSearchValue(e?.target?.value)}
                value={searchValue}
                backgroundColor={AppColors.lightBlue2}
              />
            </div>
            <div className="ml20">
              <CustomButton text="Create" onClick={handleCreate} smallBtn />
            </div>
          </div>

          <CustomTable
            columns={tableHeader}
            datas={getTableList()}
            grey
            page={page}
            rowsPerPage={itemsPerLoad}
            totalRecords={filteredTemplates?.length ?? 0}
            handleChangePage={handleChangePage}
            maxHeight="70vh"
          />
        </div>
      )}
      {createValues?.visible ? (
        <CustomModal
          open={createValues?.visible}
          onClose={onCloseCreateMod}
          header="Create"
          rightModal
          modalWidth={30}
        >
          <div className="ph20">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 blue-color">
                  Template Name <span className="red-color">*</span>
                </div>
                <CustomTextField
                  value={createValues?.name}
                  handleChange={(e) =>
                    onCreateValueChange("name", e?.target?.value)
                  }
                  fullWidth
                  error={createValues?.error}
                  helperText={
                    createValues?.error ? "This field is required" : ""
                  }
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 blue-color">
                  Type <span className="red-color">*</span>
                </div>
                <CustomSelect
                  list={diagTemplateTypes}
                  value={createValues?.type}
                  handleChange={(e) =>
                    onCreateValueChange("type", e?.target?.value)
                  }
                  select
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 blue-color mb5">
                  Linked service
                </div>
                <SearchableSelect
                  list={serviceList}
                  value={createValues?.linkedServices ?? []}
                  handleChange={(e) =>
                    onCreateValueChange(
                      "linkedServices",
                      e?.target?.value ?? [],
                    )
                  }
                  multiSelectTagCheck
                  maxFilteredOptions={100}
                  searchFields={[
                    "name",
                    "serviceName",
                    "category",
                    "subCategory",
                  ]}
                  backgroundColor={AppColors.white}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="flex1-end mt10">
                  <div>
                    <CustomButton text="Save" smallBtn onClick={onNext} />
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
        </CustomModal>
      ) : null}
      <CustomModal
        open={delModVsble}
        onClose={handleCloseDel}
        header="Delete"
        modal
        modalWidth={30}
      >
        <div className="ml10">Are you sure want to delete the template?</div>
        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleCloseDel} smallBtn />
          </div>
          <div className="ml20">
            <CustomButton
              redBtn
              text="Confirm"
              onClick={handleConfirmDel}
              smallBtn
            />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default DiagnosticTemplate;
